import { $generateHtmlFromNodes } from '@lexical/html';
import { $getRoot, createEditor, SerializedEditorState } from 'lexical';
import { HEADLESS_LEXICAL_CONFIG, toEditorState } from './ast';
import { RichInputVariant } from '@mutiny-pkg/champaign-schemas/src/schemas/richText';
import * as styles from './RichInput/lexicalConfig.css';
import { ExtendedTextNode } from '@mutiny-pkg/bin-ui/src/richtext/ExtendedTextPlugin/ExtendedTextNode';
import { resolveThemeValues } from '@mutiny-pkg/bin-ui/src/richtext/ExtendedTextPlugin/ExtendedTextPlugin';
import { ThemeProperties } from '@mutiny-pkg/champaign-schemas/src/schemas/themeProperties/types';
import { RichText } from '@mutiny-pkg/champaign-schemas/src/schemas/richText/interfaces';
import { MentionNode } from './MentionsPlugin/MentionNode';
import type { MentionItem } from './MentionsPlugin/MentionsPlugin';

// Vite provides a statically replaced env var `import.meta.env.SSR` but there are some issues
// with the type definitions so we use this more universal but less elegant approach
// We **should** use the Vite env var if we use vite to bundle so all usage of this flag can be tree-shaken
const SSR = typeof window === 'undefined';

// NOTE: JSDOM can and should only run in node so we avoid importing or bundling it in client
let JSDOM: typeof import('jsdom').JSDOM;
if (SSR) {
  JSDOM = (
    await import(
      // Webpack would try to analyze and run this import without the magic comment
      /* webpackIgnore: true */
      'jsdom'
    )
  ).JSDOM;
}

const config = {
  ...HEADLESS_LEXICAL_CONFIG,
  theme: {
    paragraph: styles.block[RichInputVariant.InlineEditing],
    link: styles.link,
  },
  editorState: undefined,
};

type RenderOptions = {
  isEditor: boolean;
  mentionItems: MentionItem[];
};

export function renderRichTextToHtml(
  richText: RichText | string,
  theme?: ThemeProperties,
  options?: RenderOptions,
): string;
export function renderRichTextToHtml(
  richText: undefined | null,
  theme?: ThemeProperties,
  options?: RenderOptions,
): string;
export function renderRichTextToHtml(
  richText: RichText | string | undefined | null,
  theme?: ThemeProperties,
  options?: RenderOptions,
): string;
export function renderRichTextToHtml(
  richText: RichText | string | undefined | null,
  theme?: ThemeProperties,
  options?: RenderOptions,
): string {
  if (richText == null) {
    return '';
  }

  return renderSerializedEditorStateToHtml(toEditorState(richText), theme, options);
}

export function renderSerializedEditorStateToHtml(
  serializedEditorState: SerializedEditorState,
  theme?: ThemeProperties,
  options?: RenderOptions,
): string {
  let cleanupDomProvider;
  if (SSR) {
    cleanupDomProvider = patchDomProvider();
  }

  try {
    return renderSerializedEditorStateToHtmlImpl(serializedEditorState, theme, options);
  } finally {
    if (cleanupDomProvider) {
      cleanupDomProvider();
    }
  }
}

function renderSerializedEditorStateToHtmlImpl(
  serializedEditorState: SerializedEditorState,
  theme?: ThemeProperties,
  options?: RenderOptions,
): string {
  const editor = createEditor(config);

  // Register transforms BEFORE setting editor state so they run on imported nodes
  if (theme) {
    editor.registerNodeTransform(ExtendedTextNode, (node) => {
      resolveThemeValues(node, theme);
    });
  }

  // Parse and set editor state AFTER registering transforms
  const editorState = editor.parseEditorState(serializedEditorState);
  editor.setEditorState(editorState);

  // Manually hydrate mention node labels from mentionItems
  // Node transforms don't run on imported nodes, so we need to explicitly update them
  if (options) {
    const itemsByValue = Object.fromEntries(options.mentionItems.map((item) => [item.value, item]));

    editor.update(() => {
      const root = $getRoot();
      const allNodes = root.getAllTextNodes();

      allNodes.forEach((node) => {
        if (node instanceof MentionNode) {
          const writableNode = node.getWritable();
          const item = itemsByValue[writableNode.getValue()];

          if (item) {
            writableNode.setLabel(item.label);
          } else {
            // Fallback: use value as label when item not found
            writableNode.setLabel(writableNode.getValue());
          }
        }
      });
    });
  }

  let result = '';
  let error = null;
  // NOTE: the "callback" seems to be executed immediately so we can just lift the values out of the closure
  editor.read(() => {
    try {
      result = $generateHtmlFromNodes(editor, null);
    } catch (e) {
      error = e;
    }
  });

  if (error) {
    throw error;
  }

  return result;
}

function patchDomProvider() {
  const dom = new JSDOM();

  const _window = global.window;
  const _document = global.document;

  // eslint-disable-next-line
  // @ts-ignore
  global.window = dom.window;
  global.document = dom.window.document;

  return () => {
    global.window = _window;
    global.document = _document;
  };
}
